// Как называется уровень сложности задания словом.
//
// ⚠️ Почему модуль лежит здесь, а не в форме выдачи домашки, где он был написан
// 18.08.2026. Словарь называет ЗНАЧЕНИЯ поля `difficulty`, а само поле объявлено
// рядом, в `./coverage.ts` и `./topics.ts`, одной схемой на оба экрана. Пока
// словарь лежал в `features/homework/assign`, у него завелась вторая копия в
// карте покрытия, и копии разошлись: форма выдачи уже говорила «средний», карта
// всё ещё «обычное». Оставить его в одной из двух фич значит либо развести
// копии снова, либо заставить карту покрытия импортировать из формы выдачи
// домашки, то есть экран-отчёт из экрана-действия, а это зависимость наоборот.
//
// Барьер видимости (5.3 CLAUDE.md) соблюдён буквально: файл не импортирует
// ничего, и из него не начинается ни одного пути к `@edu/shared/exercises`.
// Прецедент того, что показываемые слова живут в корневом экспорте, рядом:
// `import.ts` держит там же `IMPORT_ERROR_TEXT` с той же формой словаря плюс
// умолчания.

/**
 * Уровень словом: 1 лёгкий, 2 средний, 3 сложный.
 *
 * Одним модулем на весь продукт, потому что называют его пять мест сразу:
 * сегмент переключателя уровня, подпись «есть только лёгкий и средний»,
 * предпросмотр над кнопкой, текст отказа `generator_unavailable` и панель темы
 * на карте покрытия. Разъехавшиеся копии дали бы «средний» в строке и «обычный»
 * в предпросмотре, и заметил бы это ровно тот человек, которому мы это
 * показываем. Ровно так и вышло: пятое место жило своей копией и отстало на
 * переименовании.
 *
 * ⚠️ Переименовано 18.08.2026 из «базовый / обычный / повышенный». Причина не
 * вкусовая: «базовый» и «повышенный» это регистр ФИПИ, и в нём эти слова
 * означают части экзаменационной работы. Наш `difficulty` означает другое, это
 * собственная ось генераторов: у квадратных уравнений уровень 2 это целые
 * корни, а 3 дробные; у дробей 1 сложение, 2 деление. Чужой термин, не
 * обозначающий чужого понятия, обещает преподавателю соответствие ФИПИ, которого
 * в данных нет.
 *
 * ⚠️ Прилагательные, а не наречия («легко / средне / сложно»), и это требование
 * грамматики, а не стиля. Слово согласуется там, где стоит: `assign-errors.ts`
 * пишет «(средний уровень)», а `levelsLine` собирает «есть только лёгкий и
 * средний». С наречиями обе фразы ломаются, и чинить их пришлось бы
 * переписыванием предложений в двух файлах ради экономии одного знака в ширине
 * сегмента.
 *
 * ⚠️ Отсюда же требование к местам, куда словарь подставляют: слово мужского
 * рода, согласованное со словом «уровень». Панель карты покрытия писала
 * «сложность: базовое» и потому от общего словаря сломалась бы вслух
 * («сложность: средний»); её строка переписана на «уровень: средний». Новое
 * место подставляет слово к «уровню» или не подставляет вовсе.
 *
 * ⚠️ Числа приходят с сервера и в словарь могут не попасть: справочник это
 * данные, и уровень 4 однажды приедет раньше, чем правка этого файла. Поэтому
 * умолчание не пустая строка, а честное «уровень 4».
 */
export function levelName(difficulty: number): string {
  return LEVEL_NAMES[difficulty] ?? `уровень ${difficulty}`;
}

const LEVEL_NAMES: Record<number, string> = {
  1: "лёгкий",
  2: "средний",
  3: "сложный",
};

/**
 * Все уровни контракта: сегменты переключателя рисуются всегда, недоступные
 * выключены.
 *
 * ⚠️ Потолок оси записан ещё и в схемах, `difficulty: z.number().int().min(1).max(3)`
 * в `./coverage.ts` и `./topics.ts`. Четвёртый уровень поэтому это правка трёх
 * мест, а не одного, и упрётся он сначала в разбор ответа, а не в этот список.
 * Соседство с самими схемами сделано затем, чтобы это было видно глазами.
 */
export const ALL_LEVELS = [1, 2, 3];
